import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';

import { WorkflowFile } from '../../domain/workflow-file';
import { DatePropertyTimezonePipe } from '../../pipes/date-property-timezone.pipe';
import { PhobosTaskInstanceService } from '../../services/phobos-task-instance.service';
import DataUtils from '../../util/data-utils';
import { PmsRowDocumentPreviewComponent } from './pms-row-document-preview.component';
import {
   PMS_ROW_SUPPORTING_FILE_COUNT_CHANNEL,
   PmsRowSupportingFileCountChangedMessage
} from '../../domain/pms-row-supporting-file-count-event';
import { catchError, concatMap, from, map, of } from 'rxjs';

@Component({
   selector: 'app-pms-row-document-viewer',
   standalone: true,
   imports: [
      CommonModule,
      ButtonModule,
      FileUploadModule,
      DatePropertyTimezonePipe,
      PmsRowDocumentPreviewComponent
   ],
   templateUrl: './pms-row-document-viewer.component.html',
   styleUrls: ['./pms-row-document-viewer.component.scss']
})
export class PmsRowDocumentViewerComponent implements OnInit {
   @ViewChild('fileUpload')
   fileUpload?: FileUpload;

   taskInstanceId = 0;
   pmsDataId = 0;
   targetRowId = '';
   hasValidContext = false;

   supportingFiles: WorkflowFile[] = [];
   selectedFile: WorkflowFile | null = null;

   isLoading = false;
   isUploading = false;

   loadError = '';
   uploadError = '';
   uploadSuccess = '';
   downloadError = '';

   private requestVersion = 0;
   private readonly downloadingFileIds = new Set<number>();

   constructor(
      private route: ActivatedRoute,
      private phobosTaskInstanceService: PhobosTaskInstanceService,
      private destroyRef: DestroyRef
   ) {}

   ngOnInit(): void {
      this.route.paramMap
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((params) => {
            ++this.requestVersion;

            this.taskInstanceId = Number(params.get('taskInstanceId'));
            this.pmsDataId = Number(params.get('pmsDataId'));
            this.targetRowId = params.get('targetRowId')?.trim() ?? '';

            this.hasValidContext =
               Number.isInteger(this.taskInstanceId) &&
               this.taskInstanceId > 0 &&
               Number.isInteger(this.pmsDataId) &&
               this.pmsDataId > 0 &&
               this.targetRowId.length > 0;

            this.supportingFiles = [];
            this.selectedFile = null;

            this.isLoading = false;
            this.isUploading = false;

            this.loadError = '';
            this.uploadError = '';
            this.uploadSuccess = '';
            this.downloadError = '';

            this.downloadingFileIds.clear();
            this.fileUpload?.clear();

            if (!this.hasValidContext) {
               return;
            }

            this.loadSupportingFiles();
         });
   }

   get canUpload(): boolean {
      return this.hasValidContext && !this.isLoading && !this.isUploading;
   }

   loadSupportingFiles(): void {
      if (!this.hasValidContext || this.isUploading) {
         return;
      }

      const requestVersion = ++this.requestVersion;

      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;
      const targetRowId = this.targetRowId;

      const previouslySelectedFileId = this.selectedFile?.id;

      this.isLoading = true;
      this.loadError = '';

      this.phobosTaskInstanceService
         .getPmsRowSupportingFiles(taskInstanceId, pmsDataId, targetRowId)
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe({
            next: (files) => {
               if (
                  requestVersion !== this.requestVersion ||
                  !this.isCurrentDocumentContext(
                     taskInstanceId,
                     pmsDataId,
                     targetRowId
                  )
               ) {
                  return;
               }

               this.supportingFiles = files ?? [];

               this.selectedFile =
                  this.supportingFiles.find(
                     (file) =>
                        previouslySelectedFileId !== undefined &&
                        file.id === previouslySelectedFileId
                  ) ??
                  this.supportingFiles[0] ??
                  null;

               this.isLoading = false;
            },
            error: (error: HttpErrorResponse) => {
               if (
                  requestVersion !== this.requestVersion ||
                  !this.isCurrentDocumentContext(
                     taskInstanceId,
                     pmsDataId,
                     targetRowId
                  )
               ) {
                  return;
               }

               console.error('Unable to load PMS row supporting files', error);

               this.supportingFiles = [];
               this.selectedFile = null;
               this.isLoading = false;

               if (error.status === 401 || error.status === 403) {
                  this.loadError =
                     'You do not have permission to view these supporting documents.';
               } else if (error.status === 404) {
                  this.loadError =
                     'The report row or its supporting documents could not be found.';
               } else {
                  this.loadError =
                     'Unable to load supporting documents. Please try again.';
               }
            }
         });
   }

   onUpload(event: { files?: File[] }): void {
      const files = event.files ?? [];

      if (files.length === 0 || !this.canUpload) {
         return;
      }

      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;
      const targetRowId = this.targetRowId;

      const knownFileIds = new Set(
         this.supportingFiles
            .map((item) => item.id)
            .filter((id): id is number => id !== undefined)
      );

      let uploadedCount = 0;
      let failedCount = 0;
      let permissionFailure = false;

      this.isUploading = true;
      this.uploadError = '';
      this.uploadSuccess = '';

      from(files)
         .pipe(
            concatMap((file) =>
               this.phobosTaskInstanceService
                  .uploadPmsRowSupportingFile(
                     taskInstanceId,
                     pmsDataId,
                     targetRowId,
                     file
                  )
                  .pipe(
                     map((uploadedFiles) => ({
                        file,
                        uploadedFiles,
                        error: null as HttpErrorResponse | null
                     })),
                     catchError((error: HttpErrorResponse) =>
                        of({
                           file,
                           uploadedFiles: null as WorkflowFile[] | null,
                           error
                        })
                     )
                  )
            ),
            takeUntilDestroyed(this.destroyRef)
         )
         .subscribe({
            next: ({ file, uploadedFiles, error }) => {
               if (
                  !this.isCurrentDocumentContext(
                     taskInstanceId,
                     pmsDataId,
                     targetRowId
                  )
               ) {
                  return;
               }

               if (error) {
                  console.error(
                     `Unable to upload supporting document: ${file.name}`,
                     error
                  );

                  failedCount++;

                  if (error.status === 401 || error.status === 403) {
                     permissionFailure = true;
                  }

                  return;
               }

               const updatedFiles = uploadedFiles ?? [];

               const newlyUploadedFile = updatedFiles.find(
                  (item) => item.id !== undefined && !knownFileIds.has(item.id)
               );

               this.supportingFiles = updatedFiles;

               for (const item of updatedFiles) {
                  if (item.id !== undefined) {
                     knownFileIds.add(item.id);
                  }
               }

               if (newlyUploadedFile) {
                  this.selectedFile = newlyUploadedFile;
               }

               uploadedCount++;
            },
            complete: () => {
               if (
                  !this.isCurrentDocumentContext(
                     taskInstanceId,
                     pmsDataId,
                     targetRowId
                  )
               ) {
                  return;
               }

               this.isUploading = false;
               this.fileUpload?.clear();

               if (uploadedCount > 0) {
                  if (failedCount === 0) {
                     this.uploadSuccess =
                        files.length === 1
                           ? `${files[0].name} uploaded successfully.`
                           : `${files.length} documents uploaded successfully.`;
                  } else {
                     this.uploadSuccess = `${uploadedCount} of ${files.length} documents uploaded successfully.`;
                  }
               }

               if (failedCount > 0) {
                  if (permissionFailure && uploadedCount === 0) {
                     this.uploadError =
                        'You do not have permission to upload documents for this row.';
                  } else {
                     this.uploadError = `${failedCount} of ${files.length} documents could not be uploaded. Please try again.`;
                  }
               }

               this.broadcastSupportingFileCountChanged(
                  taskInstanceId,
                  pmsDataId,
                  targetRowId,
                  this.supportingFiles.length
               );
            }
         });
   }

   downloadFile(file: WorkflowFile): void {
      if (!file.id || !this.hasValidContext || this.downloadingFileIds.has(file.id)) {
         return;
      }

      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;
      const targetRowId = this.targetRowId;
      const supportingFileId = file.id;

      this.downloadingFileIds.add(supportingFileId);
      this.downloadError = '';

      this.phobosTaskInstanceService
         .getPmsRowSupportingFileSource(
            taskInstanceId,
            pmsDataId,
            targetRowId,
            supportingFileId
         )
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe({
            next: (sourceFile) => {
               this.downloadingFileIds.delete(supportingFileId);

               if (
                  !this.isCurrentDocumentContext(
                     taskInstanceId,
                     pmsDataId,
                     targetRowId
                  )
               ) {
                  return;
               }

               if (!sourceFile?.data) {
                  this.downloadError = 'The document content is unavailable.';
                  return;
               }

               try {
                  const content = DataUtils.base64ToArrayBuffer(sourceFile.data);

                  const blob = new Blob([content], {
                     type:
                        sourceFile.mimeType ||
                        file.mimeType ||
                        'application/octet-stream'
                  });

                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');

                  link.href = url;
                  link.download =
                     sourceFile.fileName || file.fileName || 'supporting-document';

                  document.body.appendChild(link);
                  link.click();
                  link.remove();

                  setTimeout(() => URL.revokeObjectURL(url), 10000);
               } catch (error) {
                  console.error('Unable to download supporting document', error);
                  this.downloadError = 'Unable to download the document.';
               }
            },
            error: (error: HttpErrorResponse) => {
               this.downloadingFileIds.delete(supportingFileId);

               if (
                  !this.isCurrentDocumentContext(
                     taskInstanceId,
                     pmsDataId,
                     targetRowId
                  )
               ) {
                  return;
               }

               console.error('Unable to download supporting document', error);

               if (error.status === 401 || error.status === 403) {
                  this.downloadError =
                     'You do not have permission to download this document.';
               } else {
                  this.downloadError =
                     'Unable to download the document. Please try again.';
               }
            }
         });
   }

   isDownloading(file: WorkflowFile): boolean {
      return !!file.id && this.downloadingFileIds.has(file.id);
   }

   selectFile(file: WorkflowFile): void {
      this.selectedFile = file;
      this.downloadError = '';
   }

   getFileIconClass(file: WorkflowFile): string {
      const mimeType = (file.mimeType ?? '').toLowerCase();
      const fileName = (file.fileName ?? '').toLowerCase();

      if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) {
         return 'pi pi-file-pdf';
      }

      if (
         mimeType.startsWith('image/') ||
         /\.(png|jpg|jpeg|gif|webp)$/i.test(fileName)
      ) {
         return 'pi pi-image';
      }

      return 'pi pi-file';
   }

   trackFileById(index: number, file: WorkflowFile): number | string {
      return file.id ?? file.fileName ?? index;
   }

   private isCurrentDocumentContext(
      taskInstanceId: number,
      pmsDataId: number,
      targetRowId: string
   ): boolean {
      return (
         this.hasValidContext &&
         this.taskInstanceId === taskInstanceId &&
         this.pmsDataId === pmsDataId &&
         this.targetRowId === targetRowId
      );
   }

   private broadcastSupportingFileCountChanged(
      taskInstanceId: number,
      pmsDataId: number,
      targetRowId: string,
      fileCount: number
   ): void {
      if (typeof BroadcastChannel === 'undefined') {
         return;
      }

      const message: PmsRowSupportingFileCountChangedMessage = {
         type: 'PMS_ROW_SUPPORTING_FILE_COUNT_CHANGED',
         taskInstanceId,
         pmsDataId,
         targetRowId,
         fileCount
      };

      try {
         const channel = new BroadcastChannel(PMS_ROW_SUPPORTING_FILE_COUNT_CHANNEL);

         channel.postMessage(message);
         channel.close();
      } catch (error) {
         console.warn('Unable to broadcast supporting document count', error);
      }
   }
}
